import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    createTheme,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Modal,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { copyObject, highlightStyle } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { AbuseHistoryModel } from '../../../Models/AbuseHistory/AbuseHistoryModel.model';
import {
    DOMESTIC_VIOLENCE,
    ELDER_ABUSE,
    fetchAbuseHistoryByMemberId,
    fetchAbuseHistoryInfo,
    fetchAbuseHistoryOptions,
    PS_ABUSE_ADULT,
    saveAbuseHistoryInfo,
    setAbuseHistoryInfo,
    SEXUAL_ABUSE,
} from '../../../store/abuseHistorySlice.slice';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { modalStyle, NO, YES } from '../../../utils/constants';
import { AbuseHistoryItem } from '../../../Models/AbuseHistory/AbuseHistoryItem.model';
import TCodeOptions from './TCodeOptions';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import SafetyPlanPromptModal from '../../SafetyPlanPromptModal/SafetyPlanPromptModal';
import { openSafetyPlanModal, setShowSafetyPlanModal } from '../../../store/behaviorOrSafetyPlanSlice.slice';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Abuse_History_Developmental_History_Mental_Health_Treatment_History.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const AbuseHistory: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { showSafetyPlanModal } = useSelector((state: RootState) => state.safetyPlanSlice);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { abuseHistoryInfo, abuseHistoryOptions } = useSelector((state: RootState) => state.abuseHistorySlice);
    const isSaving = isSavingEngagementDraft;

    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [selectedAbuseItemId, setSelectedAbuseItemId] = useState<string>('');
    const [sexualAbuseTCode, setSexualAbuseTCode] = useState<string>('');
    const [physicalAbuseTCode, setPhysicalAbuseTCode] = useState<string>('');
    const [tCodeType, setTCodeType] = useState<string>('');

    const [showModal, setShowModal] = useState(false);

    const listOfPhysicalAbuse = abuseHistoryInfo.AbuseHistoryList?.filter(
        (o) => o.Label === PS_ABUSE_ADULT || o.Label === DOMESTIC_VIOLENCE || o.Label === ELDER_ABUSE
    );

    const handleChangeCollapsingQuestion = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (value === NO) {
            dispatch(
                setAbuseHistoryInfo({
                    ...abuseHistoryInfo,
                    HistoryOfAbuse: value,
                    AbuseHistoryList: [],
                    IsAIHistoryOfAbuse: false,
                    IsAIAbuseHistoryList: false,
                })
            );
        } else {
            dispatch(
                setAbuseHistoryInfo({
                    ...abuseHistoryInfo,
                    HistoryOfAbuse: value,
                    IsAIHistoryOfAbuse: false,
                })
            );
        }
        setIsChanged(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, id } = event.target;
        const copy = copyObject(abuseHistoryInfo);
        const listItem = copy.AbuseHistoryList?.find((o) => o.Id === id);
        const abuseItem = abuseHistoryOptions.find((o) => o.Id === id);
        if (listItem) {
            copy.AbuseHistoryList = copy.AbuseHistoryList?.filter((o) => o.Id !== id);
        } else {
            const newAHItem = new AbuseHistoryItem();
            newAHItem.Id = abuseItem.Id;
            newAHItem.Label = abuseItem.Label;
            newAHItem.Zcode = abuseItem.Zcode;
            newAHItem.ZcodeDescription = abuseItem.ZcodeDescription;

            copy.AbuseHistoryList.push(newAHItem);
        }
        copy.IsAIAbuseHistoryList = false;
        dispatch(setAbuseHistoryInfo({ ...copy }));
        if (name === SEXUAL_ABUSE && copy.AbuseHistoryList?.some((o) => o.Id === id)) {
            setTCodeType(name);
            setSelectedAbuseItemId(id);
            setSexualAbuseTCode('');
            setShowModal(true);
        }
        setIsChanged(true);
    };

    const isComplete = (abuseHistoryInfo: AbuseHistoryModel) => {
        return (
            Boolean(abuseHistoryInfo.HistoryOfAbuse) &&
            Boolean(
                Boolean(abuseHistoryInfo.HistoryOfAbuse === NO) ||
                    Boolean(abuseHistoryInfo.HistoryOfAbuse === YES && abuseHistoryInfo.AbuseHistoryList?.some((o) => o.Details?.length > 0))
            )
        );
    };

    const handleSaveTCodeSelection = () => {
        const copy = copyObject(abuseHistoryInfo);
        const currentItem = copy.AbuseHistoryList.find((o) => o.Id === selectedAbuseItemId);
        if (currentItem?.Label === SEXUAL_ABUSE) {
            currentItem.Tcode = sexualAbuseTCode;
            dispatch(setAbuseHistoryInfo({ ...copy }));
            setShowModal(false);
            setSelectedAbuseItemId('');
        }
        if (currentItem?.Label !== SEXUAL_ABUSE && listOfPhysicalAbuse.length > 0) {
            const listOfIds = listOfPhysicalAbuse.map((o) => o.Id);
            const updatedList = copy.AbuseHistoryList.map((o) => {
                if (listOfIds.includes(o.Id)) {
                    return { ...o, Tcode: physicalAbuseTCode };
                }
                return o;
            });
            copy.AbuseHistoryList = updatedList;
            dispatch(setAbuseHistoryInfo({ ...copy }));
            setShowModal(false);
            setShowSaveAlert(true);
        }
    };

    const handleCloseModal = () => {
        const copy = copyObject(abuseHistoryInfo);
        const currentItem = copy.AbuseHistoryList.find((o) => o.Id === selectedAbuseItemId);
        if (currentItem?.Label === SEXUAL_ABUSE && !currentItem.Tcode) {
            copy.AbuseHistoryList = copy.AbuseHistoryList.filter((o) => o.Id !== selectedAbuseItemId);
            dispatch(setAbuseHistoryInfo({ ...copy }));
        }
        setShowModal(false);
    };

    const isSafetyPlanTriggered = (): boolean => {
        return abuseHistoryInfo?.AbuseHistoryList?.some((item) => item.Label === DOMESTIC_VIOLENCE) ?? false;
    };

    const proceedToSaveFlow = () => {
        const listOfSavedTcodes = listOfPhysicalAbuse.filter((o) => o.Tcode);
        const listOfMissingTcodes = listOfPhysicalAbuse.filter((o) => !o.Tcode);
        if (listOfPhysicalAbuse.length > 0) {
            if (listOfSavedTcodes.length > 0) {
                const firstItem = listOfSavedTcodes[0];
                setPhysicalAbuseTCode(firstItem.Tcode);
                setTCodeType(firstItem.Label);
            } else {
                setPhysicalAbuseTCode('');
                setTCodeType(listOfPhysicalAbuse[0].Label);
            }
            if (listOfMissingTcodes.length > 0) {
                setShowModal(true);
            } else {
                setShowSaveAlert(true);
            }
        } else {
            setShowSaveAlert(true);
        }
    };

    const handleSubmit = () => {
        if (isSafetyPlanTriggered()) {
            dispatch(openSafetyPlanModal());
            return;
        }
        proceedToSaveFlow();
    };

    const handleSafetyPlanModalComplete = () => {
        dispatch(setShowSafetyPlanModal(false));
        proceedToSaveFlow();
    };

    const handleOnConfirmToNext = (abuseHistoryInfo: AbuseHistoryModel, isComplete = false) => {
        const copy = copyObject(abuseHistoryInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveAbuseHistoryInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveAbuseHistoryInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setAbuseHistoryInfo(new AbuseHistoryModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        dispatch(fetchAbuseHistoryOptions());
    }, [dispatch]);

    useEffect(() => {
        if (engagementDraft?.AbuseHistoryId) {
            dispatch(fetchAbuseHistoryInfo(engagementDraft.AbuseHistoryId));
        } else {
            dispatch(fetchAbuseHistoryByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                <Grid>
                    {isSaving ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Modal
                                open={showModal}
                                onClose={() => {
                                    setShowModal(false);
                                }}
                                aria-labelledby="modal-modal-title"
                                aria-describedby="modal-modal-description"
                            >
                                <Box sx={modalStyle}>
                                    <TCodeOptions
                                        codeType={tCodeType}
                                        onClose={handleCloseModal}
                                        onSave={handleSaveTCodeSelection}
                                        physicalAbuseSelection={physicalAbuseTCode}
                                        setPhysicalAbuseSelection={setPhysicalAbuseTCode}
                                        sexualAbuseSelection={sexualAbuseTCode}
                                        setSexualAbuseSelection={setSexualAbuseTCode}
                                    />
                                </Box>
                            </Modal>

                            <Grid container spacing={2} sx={classes.root}>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant="h6">Abuse History</Typography>
                                        <Tooltip title="Abuse History Help Document">
                                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                        </Tooltip>
                                    </Stack>
                                    {isBHDiagnostic && (
                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                            * Required for BH Diagnostic
                                        </Typography>
                                    )}
                                    <hr />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel>Is or has the member been a victim of a violent crime or terrorism?</FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <RadioGroup
                                        row
                                        name="ViolentCrimeOrTerrorism"
                                        value={abuseHistoryInfo.ViolentCrimeOrTerrorism || ''}
                                        onChange={(e) => {
                                            dispatch(
                                                setAbuseHistoryInfo({
                                                    ...abuseHistoryInfo,
                                                    ViolentCrimeOrTerrorism: e.target.value,
                                                    IsAIViolentCrimeOrTerrorism: false,
                                                })
                                            );
                                            setIsChanged(true);
                                        }}
                                    >
                                        {yesNoList.map((type, index) => {
                                            return (
                                                <FormControlLabel key={`answer-label-${index}`} value={type.Answer} control={<Radio />} label={type.Answer} />
                                            );
                                        })}
                                    </RadioGroup>
                                    {abuseHistoryInfo.ViolentCrimeOrTerrorism === YES && (
                                        <TextField
                                            label="Comments"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            margin="normal"
                                            name="ViolentCrimeOrTerrorismDescription"
                                            value={abuseHistoryInfo.ViolentCrimeOrTerrorismDescription || ''}
                                            onChange={(e) => {
                                                dispatch(
                                                    setAbuseHistoryInfo({
                                                        ...abuseHistoryInfo,
                                                        ViolentCrimeOrTerrorismDescription: e.target.value,
                                                        IsAIViolentCrimeOrTerrorismDescription: false,
                                                    })
                                                );
                                                setIsChanged(true);
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel required={isBHDiagnostic} sx={isBHDiagnostic ? highlightStyle : undefined}>
                                        Have you ever experienced violence or abuse in your life, <br />
                                        including as a child?
                                    </FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <RadioGroup
                                        row
                                        name="HistoryOfAbuse"
                                        value={abuseHistoryInfo.HistoryOfAbuse || ''}
                                        onChange={(e) => {
                                            handleChangeCollapsingQuestion(e);
                                        }}
                                    >
                                        {yesNoList.map((type, index) => {
                                            return (
                                                <FormControlLabel key={`answer-label-${index}`} value={type.Answer} control={<Radio />} label={type.Answer} />
                                            );
                                        })}
                                    </RadioGroup>
                                </Grid>
                                <Collapse in={abuseHistoryInfo.HistoryOfAbuse === YES}>
                                    <Grid container p={2}>
                                        <Grid size={12} sx={{ marginLeft: 'auto', pr: 2 }}>
                                            <Stack spacing={2}>
                                                <FormGroup>
                                                    <Grid container spacing={2}>
                                                        {abuseHistoryOptions.map((option, index) => {
                                                            return (
                                                                <Grid size={6} key={`abuse-history-option-${index}`}>
                                                                    <Paper elevation={3} sx={{ p: 2 }}>
                                                                        <Stack>
                                                                            <FormControlLabel
                                                                                sx={{ pl: 2 }}
                                                                                control={
                                                                                    <Checkbox
                                                                                        id={option.Id}
                                                                                        checked={abuseHistoryInfo.AbuseHistoryList.some(
                                                                                            (o) => o.Id === option.Id
                                                                                        )}
                                                                                        value={option.Zcode}
                                                                                        onChange={handleChange}
                                                                                        name={option.Label}
                                                                                    />
                                                                                }
                                                                                label={option.Label}
                                                                            />
                                                                            <Collapse in={abuseHistoryInfo.AbuseHistoryList.some((o) => o.Id === option.Id)}>
                                                                                <TextField
                                                                                    label="Please specify"
                                                                                    variant="outlined"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    required
                                                                                    multiline
                                                                                    rows={3}
                                                                                    margin="normal"
                                                                                    value={
                                                                                        abuseHistoryInfo.AbuseHistoryList.find((o) => o.Id === option.Id)
                                                                                            ?.Details || ''
                                                                                    }
                                                                                    onChange={(e) => {
                                                                                        const copy = copyObject(abuseHistoryInfo);
                                                                                        const currentItem = copy.AbuseHistoryList.find(
                                                                                            (o) => o.Id === option.Id
                                                                                        );
                                                                                        currentItem.Details = e.target.value;
                                                                                        dispatch(setAbuseHistoryInfo({ ...copy }));
                                                                                        setIsChanged(true);
                                                                                    }}
                                                                                />
                                                                            </Collapse>
                                                                        </Stack>
                                                                    </Paper>
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </FormGroup>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </Grid>

                            <Grid justifyContent="flex-end" className="content-footer">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ marginRight: 2 }}
                                    disabled={isSaving}
                                    onClick={() => dispatch(gotoPreviousMenu())}
                                    startIcon={<ArrowBackIosIcon />}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSaving}
                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                    onClick={handleSubmit}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </>
                    )}

                    {Boolean(isComplete(abuseHistoryInfo) && abuseHistoryInfo.CreatedBy && !isChanged) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title={`Abuse History`}
                            buttonsDirection="column-reverse"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText={`Abuse History Assessment Not Reviewed`}
                            okText={`Agree With Abuse History Assessment`}
                            onConfirm={() => handleOnConfirmToNext(abuseHistoryInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={`Last Abuse History assessment completed on ${(() => {
                                const d = toDate(abuseHistoryInfo.UpdatedDate ?? abuseHistoryInfo.CreatedDate);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}.`}
                        />
                    ) : Boolean(isComplete(abuseHistoryInfo)) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Abuse History"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(abuseHistoryInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message="I confirm and acknowledge that Abuse History is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Abuse History"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(abuseHistoryInfo, false)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            renderBody={() =>
                                BHDiagnosticConfirmationBody({
                                    assessmentName: 'Abuse History',
                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                })
                            }
                        />
                    )}
                </Grid>
                <SafetyPlanPromptModal
                    open={showSafetyPlanModal}
                    onCancel={() => dispatch(setShowSafetyPlanModal(false))}
                    onComplete={handleSafetyPlanModalComplete}
                />
            </Grid>
        </Paper>
    );
};
export default AbuseHistory;
